<script lang="ts" setup>
/**
 * 投稿者プロフィール投稿フォーム用。
 */
import { reactive, toRefs, defineEmits } from "vue";
import { ReviewerAttributeAnswerPostRequest } from "@/apiRequests/models/communityTalk/reviewerAttributeAnswerPostRequest";
import { ReviewerAttributeDto } from "@/apiRequests/models/communityTalk/talkRoomGetResponse";

interface State {
	/**
	 * 選択された投稿者プロフィールID
	 */
	reviewerAttributeId: string;
	/**
	 * 選択された投稿者プロフィール選択肢ID
	 */
	attributeChoiceId: string;
}

// reactiveな変数宣言
const state = reactive<State>({
	reviewerAttributeId: "",
	attributeChoiceId: "",
});
const { reviewerAttributeId, attributeChoiceId } = toRefs(state);

interface Props {
	/**
	 * 投稿者プロフィールデータ。
	 */
	reviewerAttribute: ReviewerAttributeDto;
	/**
	 * プリセット用の投稿者プロフィールデータ。
	 */
	reviewerAttributePreset: ReviewerAttributeAnswerPostRequest | undefined;
	/**
	 * 識別子ID。
	 * 各投稿者プロフィール投稿フォームでinputタグのID値を分別化するために使用。
	 */
	uniqueKey: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-undef
const props = defineProps<Props>();

interface Emits {
	(e: "click-reviewer-attributettribute-choice", v: { reviewerAttributeId: string; attributeChoiceId: string; checked: boolean }): void;
}
const emit = defineEmits<Emits>();

const onclick = (element: HTMLInputElement, reviewerAttributeId: string, attributeChoiceId: string): void => {
	// 対象の直前の選択状態を取得
	const beforeChecked = isCheckedReviewerAttributeChoice(attributeChoiceId);

	// 親コンポーネントに選択された投稿者プロフィール情報を渡す
	if (beforeChecked) {
		emit("click-reviewer-attributettribute-choice", { reviewerAttributeId: reviewerAttributeId, attributeChoiceId: attributeChoiceId, checked: false });
	} else {
		emit("click-reviewer-attributettribute-choice", { reviewerAttributeId: reviewerAttributeId, attributeChoiceId: attributeChoiceId, checked: true });
	}
};

/**
 * 対象の選択肢が選択中であるかどうか。
 */
const isCheckedReviewerAttributeChoice = (reviewerAttributeChoiceId: string): boolean => {
	// プリセットデータがなければ問答無用で`false`
	if (!props.reviewerAttributePreset) {
		return false;
	}

	const findIndex = props.reviewerAttributePreset.AttributeChoiceIds.findIndex((e) => e === reviewerAttributeChoiceId);
	if (findIndex > -1) {
		return true;
	}
	return false;
};
</script>

<template>
	<ul class="revico-community-talk-post--reviewer-attribute-choice">
		<li class="revico-community-talk-post--reviewer-attribute-choice-item" v-for="(choice, index) in reviewerAttribute.reviewerAttributeChoices" :key="index">
			<input
				:id="uniqueKey + '_' + choice.attributeChoiceId"
				class="revico-community-talk-post--reviewer-attribute-answer"
				:name="reviewerAttribute.reviewerAttributeId"
				type="radio"
				:checked="isCheckedReviewerAttributeChoice(choice.attributeChoiceId)"
				@click="onclick($event.target as HTMLInputElement, reviewerAttribute.reviewerAttributeId, choice.attributeChoiceId)"
			/>
			<label
				:class="
					isCheckedReviewerAttributeChoice(choice.attributeChoiceId)
						? 'revico-community-talk-post--reviewer-attribute-answer-label selected'
						: 'revico-community-talk-post--reviewer-attribute-answer-label'
				"
				:for="uniqueKey + '_' + choice.attributeChoiceId"
				>{{ choice.displayName }}
			</label>
		</li>
	</ul>
</template>

<style lang="scss">
.revico-widget .revico-community-talk-post {
	&--reviewer-attribute-choice {
		display: flex;
		flex-wrap: wrap;
		&-item {
			margin: 0 4px 4px 0;
			min-height: 24px;
		}
	}
	&--reviewer-attribute-answer {
		display: none;
		&-label {
			display: block;
			font-size: 0.8em;
			padding: 4px 8px;
			background-color: #fff;
			border: 1px solid #ccc;
			border-radius: 15px;
			cursor: pointer;
			user-select: none;
			transition: 0.3s;
			&.selected {
				color: #fff;
				background-color: #696969;
				border: 1px solid #ccc;
				transition: 0.3s;
			}
		}
	}
}
</style>
